import React, {
	useEffect,
	useState,
	useContext,
	useRef,
	useMemo,
	useCallback
} from 'react';
import { createPortal } from 'react-dom';
import { setClassNames } from '@ddc/classnames-js';
import { usePrefs, useLabels } from 'wsm-common-data';
import { LoadingBox } from 'wsm-ai-loader';
import { g } from '../global-constants';
import useFocusTrap from '../hooks/useFocusTrap';
import InputDisplay from './InputDisplay';
import InventoryTabs from './InventoryTabs';
import RecentSearches from './RecentSearches';
import shouldShowRecentSearches from '../util/shouldShowRecentSearches';
import hasEmptyResults from '../util/hasEmptyResults';
import hasResults from '../util/hasResults';
import useAriaLive from '../hooks/useAriaLive';
import isEnoughAddData from '../util/isEnoughAddData';
import { SearchContext, UIContext, ResultsContext } from '../contexts';
import InventoryStandard from './InventoryStandard';

const CLOSE_CLASS_NAMES = [
	'btn-unstyled text-link-muted',
	'py-0',
	'px-0',
	'my-3',
	'border-neutral-0-alpha-3',
	'float-right'
];

const FlyOut = () => {
	const flyOutRef = useRef(null);

	const {
		searchTerm,
		numberOfCharactersForFirstSearchNumber,
		fetchingResults,
		isNLSFetchingResults
	} = useContext(SearchContext);

	const {
		openButtonRef,
		closeModal,
		inputElementRef,
		modalIsOpen,
		isMobile,
		screenWidth,
		setIsEmbeddedOverrideActive,
		doNotRenderResults
	} = useContext(UIContext);
	const labels = useLabels();

	const {
		recentSearches,
		pageSearchResults,
		preparedInventoryResults,
		handleLinkClick,
		addInfo,
		richContentToDisplay,
		showTabs,
		hasSingleVehicle
	} = useContext(ResultsContext);

	const { searchPlaceholderLabel } = usePrefs();

	const [localmodalIsOpen, setLocalmodalIsOpen] = useState(false);

	const { suggestions, vehicles, allVehiclesPath } =
		preparedInventoryResults || {};

	const isSearchTermLongEnough = useMemo(
		() => searchTerm?.length >= numberOfCharactersForFirstSearchNumber,
		[searchTerm, numberOfCharactersForFirstSearchNumber]
	);

	const hasResultsLocal = useMemo(
		() => hasResults(pageSearchResults, suggestions, vehicles),
		[pageSearchResults, suggestions, vehicles]
	);

	const hasEmptyResultsLocal = useMemo(
		() => hasEmptyResults(pageSearchResults, suggestions, vehicles),
		[pageSearchResults, suggestions, vehicles]
	);

	const announceText = useAriaLive(
		isSearchTermLongEnough,
		hasResultsLocal,
		hasEmptyResultsLocal
	);

	const hasRichContentToDisplay = useMemo(
		() => isEnoughAddData(richContentToDisplay, addInfo),
		[richContentToDisplay, addInfo]
	);

	const shouldRenderNoResults = useMemo(
		() =>
			!hasResultsLocal &&
			hasEmptyResultsLocal &&
			!hasRichContentToDisplay &&
			!fetchingResults,
		[
			hasResultsLocal,
			hasEmptyResultsLocal,
			hasRichContentToDisplay,
			fetchingResults
		]
	);

	useFocusTrap(
		[openButtonRef, flyOutRef],
		null,
		flyOutRef,
		null,
		openButtonRef,
		modalIsOpen,
		closeModal
	);

	const isWideScreen = screenWidth > 768;

	const classNames = useMemo(
		() => [
			'sts-search-flyout',
			'box',
			'pt-4 pb-4 pl-5 pr-5',
			localmodalIsOpen && 'slide-left',
			!isMobile && !isWideScreen ? 'one-column' : isMobile && 'mobile',
			isWideScreen && 'two-columns'
		],
		[localmodalIsOpen, isMobile, isWideScreen]
	);

	const viewLinkLabel = useMemo(
		() => labels.get('VIEW_ALL_VEHICLES_SITE_SEARCH'),
		[labels]
	);

	const handleClick = useCallback(() => {
		handleLinkClick('allInventoryLink', viewLinkLabel, '');
	}, [handleLinkClick, viewLinkLabel]);

	const onClose = useCallback(() => {
		setIsEmbeddedOverrideActive(false);
		closeModal();
	}, [setIsEmbeddedOverrideActive, closeModal]);

	const preventEventPropagation = useCallback((e) => {
		e.stopPropagation();
	}, []);

	const ariaLabelledBy = useMemo(() => {
		if (searchTerm) {
			return 'search-modal-headline';
		} else if (recentSearches?.length > 0) {
			return 'recent-searches-heading';
		}
		return 'default-flyout-label';
	}, [searchTerm, recentSearches]);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setLocalmodalIsOpen(true);
		}, 50);

		if (modalIsOpen) {
			document.body.style.overflow = 'hidden';
			inputElementRef.current?.focus();
		}

		return () => {
			clearTimeout(timeoutId);
		};
	}, [modalIsOpen, inputElementRef]);

	const showNLSLoading = useMemo(
		() => !fetchingResults && isNLSFetchingResults,
		[fetchingResults, isNLSFetchingResults]
	);

	const shouldRenderFlyoutContent =
		isSearchTermLongEnough && !doNotRenderResults;

	const flyOutContent = (
		<>
			{/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
			<div
				className={setClassNames(['sts-search-modal', 'clearfix'])}
				role="none"
				data-testid="sts-flyout-background"
				onClick={() => onClose(g.CLOSE_BUTTON)}
				data-widget-name="ws-site-text-search"
			/>
			<div
				className={setClassNames(classNames)}
				role="dialog"
				aria-labelledby={ariaLabelledBy}
				ref={flyOutRef}
				tabIndex="-1"
				data-testid="sts-flyout"
				data-widget-name="ws-site-text-search"
			>
				<div
					id="default-flyout-label"
					className={setClassNames(['sr-only'])}
					aria-live="polite"
					aria-atomic="true"
				>
					{labels.get(searchPlaceholderLabel)}
				</div>
				<div
					aria-live="assertive"
					className={setClassNames(['sr-only'])}
				>
					{announceText}
				</div>
				<div
					className={setClassNames([
						'pl-0',
						'py-3',
						'pr-0',
						'clearfix'
					])}
				>
					<h2
						className={setClassNames([
							'ddc-heading-3',
							'font-weight-bold',
							'text-capitalize',
							'float-left',
							'mt-3',
							'ddc-font-size-xlarge'
						])}
						id="search-modal-headline"
					>
						{labels.get('SEARCH')}
					</h2>
					<button
						type="button"
						tabIndex="0"
						aria-label={labels.get('CLOSE_MODAL')}
						className={setClassNames(CLOSE_CLASS_NAMES)}
						data-testid="flyout-close-button"
						onClick={(e) => {
							preventEventPropagation(e);
							onClose(g.CLOSE_BUTTON);
						}}
						onMouseDown={preventEventPropagation}
						onTouchStart={preventEventPropagation}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								preventEventPropagation(e);
								onClose(g.CLOSE_BUTTON);
								setTimeout(() => {
									openButtonRef.current?.focus(); // Focus on the button after a delay
								}, 10);
							}
						}}
					>
						<i
							className={setClassNames([
								'ddc-icon',
								'ddc-icon-remove2',
								'icon-size-2'
							])}
							aria-hidden="true"
						/>
					</button>
				</div>

				<InputDisplay source="flyout" />
				<div className={setClassNames(['mt-5', 'clearfix'])}>
					{shouldShowRecentSearches(
						searchTerm,
						hasResultsLocal,
						hasEmptyResultsLocal,
						recentSearches
					) && <RecentSearches preventModalClose />}

					{showNLSLoading && (
						<LoadingBox vectorName="sparkleTwo" isGradient />
					)}

					{shouldRenderFlyoutContent && (
						<div>
							<h2
								id="results-heading"
								className={setClassNames(['sr-only'])}
							>
								{labels.get('SEARCH_RESULTS')}
							</h2>
							{showTabs ? (
								<InventoryTabs
									hasRichContentToDisplay={
										hasRichContentToDisplay
									}
									shouldRenderNoResults={
										shouldRenderNoResults
									}
								/>
							) : (
								<>
									{allVehiclesPath &&
										!shouldRenderNoResults &&
										!hasSingleVehicle && (
											<a
												onClick={handleClick}
												href={allVehiclesPath}
												className={setClassNames([
													'font-weight-bold',
													'py-3',
													'px-2'
												])}
											>
												<span>
													{viewLinkLabel}
													<i
														className={setClassNames(
															[
																'ddc-icon',
																'ddc-icon-arrow-details',
																'ml-3'
															]
														)}
														aria-hidden="true"
													/>
												</span>
											</a>
										)}
									<div className={setClassNames(['row'])}>
										<InventoryStandard
											vehicles={vehicles}
											suggestions={suggestions}
											shouldRenderNoResults={
												shouldRenderNoResults
											}
											hasRichContentToDisplay={
												hasRichContentToDisplay
											}
										/>
									</div>
								</>
							)}
						</div>
					)}
				</div>
			</div>
		</>
	);

	return createPortal(flyOutContent, document.body);
};

export default React.memo(FlyOut);
